import React, {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { EditorView } from "prosemirror-view";
import { Box } from "@chakra-ui/react";

import "prosemirror-view/style/prosemirror.css";

const wrapperStyles = {
  ".ProseMirror.ProseMirror-empty::before": {
    position: "absolute",
    content: "attr(data-placeholder)",
    color: "gray.200",
  },
  "[contenteditable]": {
    userSelect: "text", // needed for content editable to work in Safari
  },
};

const buildProps = (props) => ({
  ...props,
  dispatchTransaction(transaction) {
    if (props.dispatchTransaction) {
      props.dispatchTransaction(transaction);
    } else if (props.onChange) {
      props.onChange(this.state.apply(transaction));
    }
  },
});

const ProseMirror = forwardRef((props, ref) => {
  const rootRef = useRef(null);
  const viewRef = useRef(null);
  const initialPropsRef = useRef(props);
  const { state, placeholder, onFocus, onBlur, ...restProps } = props;

  useEffect(() => {
    const {
      state: initialPropsState,
      placeholder: initialPlaceholder,
      onFocus: initialOnFocus,
      onBlur: initialOnBlur,
      ...initialPropsRest
    } = initialPropsRef.current;
    const view = new EditorView(rootRef.current, {
      state: initialPropsState,
      ...buildProps(initialPropsRest),
    });

    viewRef.current = view;

    return () => {
      view.destroy();
    };
  }, []);

  useEffect(() => {
    viewRef.current?.setProps(buildProps(restProps));
  }, [restProps]);

  useLayoutEffect(() => {
    viewRef.current?.updateState(state);
  }, [state]);

  useEffect(() => {
    viewRef.current?.dom?.setAttribute("data-placeholder", placeholder || "");
  }, [placeholder]);

  useEffect(() => {
    viewRef.current?.dom?.classList?.toggle(
      "ProseMirror-empty",
      viewRef.current?.dom?.textContent?.length === 0
    );
  }, [state, viewRef.current?.dom?.textContent]);

  useImperativeHandle(ref, () => ({
    get view() {
      return viewRef.current;
    },
  }));

  return (
    <Box
      ref={rootRef}
      onFocus={onFocus}
      onBlur={onBlur}
      sx={wrapperStyles}
    />
  );
});

ProseMirror.propTypes = {
  state: PropTypes.shape({}).isRequired,
  placeholder: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
};

ProseMirror.defaultProps = {
  placeholder: undefined,
  onFocus: undefined,
  onBlur: undefined,
};

export default memo(ProseMirror);
